import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectLoading } from "modules/notification";
import { getInventoryBoxListing } from "modules/stockflow/redux/action/stockflowInventory.action";
import BoxListingComponent from "./boxListing.component";
import {
  changeBulkSelectedAvailableBox,
  changeBulkSelectedBox,
  changeSelectedBox,
  resetBoxList,
} from "modules/stockflow/redux";
import { generatePaginationOptions, updateUrlQueryParam } from "lib/helper";
import { fetchTagsDropdown } from "modules/serial-number/redux";
import { exportBoxListing } from "app/api/stockflow/inventory/exportBoxListing.api";
import { useHistory } from "react-router-dom";
import {AccessRights} from "lib/constants/accessRights";
import {canAccess} from "../../../../profile/redux/selector/profile.selector";

function BoxListingContainer() {
  const dispatch = useDispatch();
  const history = useHistory();

  const language = useSelector(state => state.profile.language)
  const hasStockflowInventoryViewAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.TrackcoPortal.BOXES));
  const hasStockflowDealerViewAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.TrackcoPortal.DEALER_READ));
  const lang = useSelector((state) => state.constant.languages);
  const token = useSelector((state) => state.session.accessToken);
  const isFetching = useSelector((state) =>
    selectLoading(state, getInventoryBoxListing.typePrefix)
  );
  const totalFiltered = useSelector(
    (state) => state.stockflowInventory.totalFiltered
  );
  const totalRecords = useSelector(
    (state) => state.stockflowInventory.totalRecords
  );
  const boxes = useSelector((state) => state.stockflowInventory.boxes);
  const tags = useSelector((state) => state.serialNumberTag.tagsDropdown);
  const selectedIds = useSelector(
    (state) => state.stockflowInventory.selectedId
  );
  const isSelectAll = useSelector(
    (state) => state.stockflowInventory.isSelectAll
  );
  const isError = useSelector(
    (state) => state.stockflowInventory.isLoadingBoxListError
  );
  const isSearchResult = boxes.length < totalRecords;
  const [paginationOptions, setPaginationOptions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFilterUpdated, setIsFilterUpdated] = useState(false);
  const [tableFilter, setTableFilter] = useState({
    page: 0,
    rowsPerPage: 25,
    search: "",
    searchColumn: "",
    order: "desc",
    orderBy: "updated_at",
    nodeIds: [],
    branch: [],
    products: [],
    createdAt: "",
    updatedAt: "",
    locatedAt: [],
  });

  const query = new URLSearchParams(window.location.search);
  const tableFilterInitialRef = useRef(true);

  useEffect(() => {
    return () => {
      dispatch(resetBoxList());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchTagsDropdown());

    const isFilter = query.has("is_filter");
    let filter = sessionStorage.getItem("inventory_box_filter");

    let temp = { ...tableFilter };
    if (isFilter && filter) {
      temp = JSON.parse(filter);
      setIsFilterUpdated(true);
      setIsFilterOpen(true);
    }

    if (isFilter) {
      updateUrlQueryParam(null, ["is_filter"]);
    }
    sessionStorage.removeItem("inventory_box_filter");

    setTableFilter(temp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tableFilterInitialRef.current) {
      getData();
    }
    tableFilterInitialRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFilter]);

  useEffect(() => {
    setPaginationOptions(generatePaginationOptions(totalFiltered));
  }, [totalFiltered]);

  const getData = () => {
    dispatch(
      getInventoryBoxListing({
        length: tableFilter.rowsPerPage,
        start: tableFilter.rowsPerPage * tableFilter.page,
        order: tableFilter.order,
        order_by: tableFilter.orderBy,
        filter: {
          search: tableFilter.search,
          search_by: tableFilter.searchColumn,
          nodes: tableFilter.nodeIds,
          products: tableFilter.products,
          created_at: tableFilter.createdAt,
          updated_at: tableFilter.updatedAt,
          located_at: tableFilter.locatedAt,
        },
      })
    );
  };

  const handleChangePage = (event, newPage) => {
    let newData = {
      ...tableFilter,
      page: newPage,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const handleChangeRowsPerPage = (event) => {
    let newData = {
      ...tableFilter,
      page: 0,
      rowsPerPage: event.target.value,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const handleSearch = (filter) => {
    let nodeIds =
      filter.branch.length > 0 ? filter.branch.map((br) => br.node_id) : [];

    let newData = {
      ...tableFilter,
      page: 0,
      search: filter.search,
      searchColumn: filter.searchColumn,
      nodeIds: nodeIds,
      branch: filter.branch,
      products: filter.products,
      createdAt: filter.createdAt,
      updatedAt: filter.updatedAt,
      locatedAt: filter.locatedAt,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const clearSearch = () => {
    let newData = {
      ...tableFilter,
      page: 0,
      search: "",
      searchColumn: "",
      nodeIds: [],
      branch: [],
      products: [],
      createdAt: "",
      updatedAt: "",
      located_at: [],
    };
    setIsFilterUpdated(false);
    setTableFilter(newData);
  };

  const handleSelectBox = (index, isSelected) => {
    dispatch(changeSelectedBox({ index, isSelected }));
  };

  const handleSelectAvailable = () => {
    dispatch(changeBulkSelectedAvailableBox({ value: true }));
  };

  const handleSelectAll = () => {
    dispatch(changeBulkSelectedBox({ value: true }));
  };

  const handleDeselectAll = () => {
    dispatch(changeBulkSelectedBox({ value: false }));
  };

  const changeOrder = (orderBy, order) => {
    let newData = {
      ...tableFilter,
      order,
      orderBy,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const resetFilter = () => {
    let newData = {
      ...tableFilter,
      page: 0,
      nodeIds: [],
      branch: [],
      products: [],
      createdAt: "",
      updatedAt: "",
      locatedAt: [],
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const exportListing = () => {
    setIsExporting(true);
    exportBoxListing
      .post({
        token,
        isSelectAll,
        selectedIds,
        filter: {
          search: tableFilter.search,
          search_by: tableFilter.searchColumn,
          status: tableFilter.status,
          nodes: tableFilter.nodeIds,
          pattern: tableFilter.pattern,
          created_at: tableFilter.createdAt,
          updated_at: tableFilter.updatedAt,
          located_at: tableFilter.locatedAt,
        },
      })
      .then((blob) => {
        saveFile(blob);
        setIsExporting(false);
      })
      .catch(() => {
        setIsExporting(false);
      });
  };

  const saveFile = async (blob) => {
    const a = document.createElement("a");
    a.download = `inventory_boxes.xlsx`;
    a.href = URL.createObjectURL(blob);
    a.addEventListener("click", (e) => {
      setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
    });
    a.click();
  };

  const handleViewDetail = (url) => {
    if (isFilterUpdated) {
      updateUrlQueryParam({ is_filter: true });
      sessionStorage.setItem(
        "inventory_box_filter",
        JSON.stringify(tableFilter)
      );
    }
    history.push(url);
  };

  return (
    <>
      <BoxListingComponent
        hasStockflowInventoryViewAccessRight={
          hasStockflowInventoryViewAccessRight
        }
        hasStockflowDealerViewAccessRight={hasStockflowDealerViewAccessRight}
        boxes={boxes}
        isFetching={isFetching}
        selectedIds={selectedIds}
        isSelectAll={isSelectAll}
        selectedCount={
          !isSelectAll ? selectedIds.length : totalFiltered - selectedIds.length
        }
        isError={isError}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={tableFilter.page}
        rowsPerPage={tableFilter.rowsPerPage}
        totalFiltered={totalFiltered}
        handleSearch={handleSearch}
        clearSearch={clearSearch}
        handleReload={getData}
        language={language}
        isSearchResult={isSearchResult}
        handleSelectBox={handleSelectBox}
        handleSelectAvailable={handleSelectAvailable}
        handleSelectAll={handleSelectAll}
        handleDeselectAll={handleDeselectAll}
        token={token}
        paginationOptions={paginationOptions}
        tableFilter={tableFilter}
        changeOrder={changeOrder}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        tags={tags}
        exportListing={exportListing}
        isExporting={isExporting}
        lang={lang}
        resetFilter={resetFilter}
        handleViewDetail={handleViewDetail}
      />
    </>
  );
}

export default BoxListingContainer;
